// src/hooks/useAudios.ts

import { useState, useEffect, useCallback } from 'react';
import { AudioFilters, AudioWithUrl } from '../types/audio.types';
import { getAudios, getAudioById } from '../services/audioService';
import { getSignedUrlCached } from '../services/signedUrlService';
import { useAuth } from '../contexts/AuthContext';

export const useAudios = (initialFilters: AudioFilters = {}) => {
  const { currentUser } = useAuth();
  const [audios, setAudios] = useState<AudioWithUrl[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<AudioFilters>(initialFilters);
  const [selectedAudio, setSelectedAudio] = useState<AudioWithUrl | null>(null);

  /**
   * Carrega áudios do Firestore com filtros aplicados
   */
  const loadAudios = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const fetchedAudios = await getAudios(filters, 10000);
      setAudios(fetchedAudios.map(audio => ({ ...audio })));
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar áudios');
      setAudios([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  /**
   * Atualiza filtros e recarrega
   */
  const updateFilters = useCallback((newFilters: AudioFilters) => {
    setFilters(newFilters);
  }, []);

  /**
   * Limpa todos os filtros
   */
  const clearFilters = useCallback(() => {
    setFilters({});
  }, []);

  /**
   * Seleciona um áudio e carrega sua signed URL
   */
  const selectAudio = useCallback(async (audioId: string) => {
    if (!currentUser) {
      setError('Usuário não autenticado');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const audio = await getAudioById(audioId);
      
      if (!audio) {
        throw new Error('Áudio não encontrado');
      }

      const signedUrl = await getSignedUrlCached(
        audioId,
        currentUser.uid,
        currentUser.email || null
      );

      const audioWithUrl: AudioWithUrl = {
        ...audio,
        signedUrl,
        signedUrlExpiry: new Date(Date.now() + 3600000)
      };

      setSelectedAudio(audioWithUrl);
      
    } catch (err: any) {
      console.error('Erro ao selecionar áudio:', err);
      setError(err.message || 'Erro ao carregar áudio');
      setSelectedAudio(null);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  /**
   * Desmarca áudio selecionado
   */
  const deselectAudio = useCallback(() => {
    setSelectedAudio(null);
  }, []);

  /**
   * Recarrega a lista quando filtros mudam
   */
  useEffect(() => {
    loadAudios();
  }, [loadAudios]);

  return {
    audios,
    loading,
    error,
    filters,
    selectedAudio,
    updateFilters,
    clearFilters,
    loadAudios,
    selectAudio,
    deselectAudio
  };
};
